import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { authInterceptor } from "./auth-interceptor";
import { inactivityManager } from "./inactivity-manager";

// Backend API base URL
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // Check for trial expiration first (403 with trialExpired flag)
    const wasTrialExpired = await authInterceptor.checkTrialExpired(res);
    if (wasTrialExpired) {
      // Redirect handled by authInterceptor, just throw to stop execution
      throw new Error('Trial expired - redirecting to billing');
    }
    
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: RequestInit
): Promise<Response> {
  // If the URL starts with /api, use it directly (Vite proxy will handle it)
  const fullUrl = url.startsWith('/api') ? url : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  
  const res = await fetch(fullUrl, {
    method,
    headers: authInterceptor.addAuthHeaders(data ? { "Content-Type": "application/json" } : {}),
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    ...options // Permitir opciones adicionales como signal para AbortController
  });

  // Check for 401/403 errors BEFORE throwing
  if (res.status === 401 || res.status === 403) {
    // Check for trial expiration first
    const wasTrialExpired = await authInterceptor.checkTrialExpired(res);
    if (wasTrialExpired) {
      throw new Error('Trial expired - redirecting to billing');
    }
    
    // For 401 errors on time clock endpoints, don't redirect - just throw error
    const isTimeclockEndpoint = url.includes('/timeclock/');
    
    if (res.status === 401 && isTimeclockEndpoint) {
      console.warn('401 Unauthorized on time clock endpoint - not redirecting', { url });
      // Still throw error so mutation handlers can handle it, but don't redirect
      const text = (await res.text()) || res.statusText;
      throw new Error(`401: ${text}`);
    }
    
    // For other 401 errors, redirect to login
    if (res.status === 401 && !isTimeclockEndpoint && window.location.pathname !== '/simple-login') {
      console.warn('401 Unauthorized - redirecting to login', { url });
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      window.location.href = '/simple-login';
      const text = (await res.text()) || res.statusText;
      throw new Error(`401: ${text}`);
    }
  }

  // Record activity on successful API calls (but only if response is OK)
  if (res.ok) {
    inactivityManager.recordActivity();
  }

  await throwIfResNotOk(res);
  return res;
}

export async function fetchWithBaseUrl(url: string, options?: RequestInit) {
  const fullUrl = url.startsWith('http') ? url : (url.startsWith('/api') ? url : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`);
  return fetch(fullUrl, options);
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    const fullUrl = url.startsWith('/api') ? url : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    
    const res = await fetch(fullUrl, {
      credentials: "include",
      headers: authInterceptor.addAuthHeaders(),
    });

    // If response is OK (200-299), proceed normally
    if (res.ok) {
      // Record activity on successful query responses
      inactivityManager.recordActivity();
      return await res.json();
    }

    // Only check for auth errors if response is not OK
    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    // Check for 401/403 errors
    if (res.status === 401 || res.status === 403) {
      // Check for trial expiration first
      const wasTrialExpired = await authInterceptor.checkTrialExpired(res);
      if (wasTrialExpired) {
        throw new Error('Trial expired');
      }
      
      // For 401, redirect to login (but don't throw error for Time Clock queries)
      if (res.status === 401) {
        const urlStr = Array.isArray(queryKey) ? queryKey[0] : String(queryKey[0] || '');
        const isTimeclockQuery = urlStr.includes('/timeclock/');
        
        console.warn('401 Unauthorized detected', { url: urlStr, isTimeclockQuery });
        
        // Don't redirect if already on login page or if we're in the middle of logging out
        if (window.location.pathname !== '/simple-login') {
          // For Time Clock queries, just return null instead of redirecting
          if (isTimeclockQuery) {
            console.warn('Time Clock query got 401 - returning null instead of redirecting');
            return null as T;
          }
          
          // For other queries, redirect
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('accessToken');
          window.location.href = '/simple-login';
        }
        
        // Don't throw error for Time Clock queries - just return null
        if (!isTimeclockQuery) {
          throw new Error('Unauthorized');
        }
        return null as T;
      }
    }

    // For other errors, use the standard error handler
    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 5000, // Considerar los datos obsoletos después de 5 segundos
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
